import { useQuery } from '@tanstack/react-query';
import { useShakeToReport } from '../hooks/useShakeToReport.js';

import { lazy, Suspense, useCallback, useMemo, useRef, useState, type JSX } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { api, logoutCurrentSession } from '../api/client.js';
import { DemoBanner } from '../components/DemoBanner.js';
import { DEMO_ACTIVE, loadSeedPlan } from '../demo/demo-mode.js';
import { HeaderBar } from '../components/HeaderBar.js';
import {
  BellIcon,
  BookmarkIcon,
  ComposeIcon,
  HomeIcon,
  MessageIcon,
  ScaleIcon,
  SearchIcon,
  SettingsIcon,
  ShieldIcon,
  UserIcon,
} from '../components/icons/Icons.js';
import { OfflineBanner } from '../components/OfflineBanner.js';
import { PrivacyNoticeBanner } from '../components/PrivacyNoticeBanner.js';
import { ProfileMenu } from '../components/ProfileMenu.js';
import { ThumbNavFab } from '../components/ThumbNavFab.js';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts.js';
import { useSession } from '../hooks/useSession.js';
import { WEB_UNREAD_BADGE_POLL_MS } from '../lib/poll-intervals.js';
import { useAppBadge } from '../pwa/useAppBadge.js';
import styles from './RootLayout.module.css';

const NAV_LINK_CLASS = ({ isActive }: { isActive: boolean }): string =>
  isActive ? `${styles['navLink']} ${styles['active']}` : (styles['navLink'] ?? '');

/** The landing page is its own lazy chunk (`LandingRoute.tsx`): the signed-in app never loads it. */
const LandingRoute = lazy(() =>
  import('./LandingRoute.js').then((module) => ({ default: module.LandingRoute })),
);

/** Self-hosted nodes that want the public timeline at `/` for signed-out visitors can build with
 * `VITE_PATCHES_LANDING=off` (docs/operations/web.md). */
/** The sandbox setup screen pulls in the E2EE runtime, so only a fresh sandbox loads it. */
const DemoSetupGate = lazy(() => import('../components/DemoSetupGate.js'));

const LANDING_ENABLED = (import.meta.env['VITE_PATCHES_LANDING'] as string | undefined) !== 'off';

/**
 * Signed-out visitors at `/` get the landing page, full-bleed without the app shell; everyone
 * else (and every other path) gets the app. The decision uses only local state, so it renders
 * with no network round trip.
 */
export function RootLayout(): JSX.Element {
  const session = useSession();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // Decided once per page load: a fresh sandbox still has its seed plan, and a reload while it
  // is being sealed lands back on the setup screen, which resumes rather than starting over.
  const [settingUp, setSettingUp] = useState(() => DEMO_ACTIVE && loadSeedPlan() !== undefined);
  const finishSetUp = useCallback(() => {
    setSettingUp(false);
    void navigate('/messages', { replace: true });
  }, [navigate]);
  if (settingUp && session !== null) {
    return (
      <Suspense fallback={null}>
        <DemoSetupGate onFinished={finishSetUp} />
      </Suspense>
    );
  }
  if (LANDING_ENABLED && session === null && pathname === '/') {
    return (
      <Suspense fallback={null}>
        <LandingRoute />
      </Suspense>
    );
  }
  return <AppShell />;
}

function AppShell(): JSX.Element {
  useShakeToReport();
  const session = useSession();
  const navigate = useNavigate();
  const helpRef = useRef<HTMLDialogElement>(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const unreadQuery = useQuery({
    // Keep cached unread data scoped to its actor so switching accounts never reuses a prior
    // account's count for either the in-app indicator or the optional OS app badge.
    queryKey: ['notifications', 'unread-count', session?.actor.id],
    queryFn: () => api.notifications.getUnreadCount({}),
    enabled: session !== null,
    // ADR 0032 §1's published unread-badge SLA; single source of truth in
    // `lib/poll-intervals.ts` (P19-021).
    refetchInterval: WEB_UNREAD_BADGE_POLL_MS,
  });

  const unreadCount = unreadQuery.data?.count ?? 0;

  // Sync the current actor's unread notification count with the optional PWA App Badging API.
  useAppBadge(unreadCount, session?.actor.id);

  useKeyboardShortcuts({
    c: () => void navigate('/compose'),
    '/': () => void navigate('/search'),
    '?': () => (helpRef.current?.open ? helpRef.current.close() : helpRef.current?.showModal()),
  });

  // P301: RootLayout re-renders on every unread-badge poll tick (WEB_UNREAD_BADGE_POLL_MS,
  // 30s) and every `profileMenuOpen` toggle — neither has anything to do with the matched
  // route. `<Outlet />` created fresh in JSX on every RootLayout render would re-invoke the
  // current route's component on each of those unrelated re-renders (e.g. re-rendering a
  // whole PostTimeline's PostCards, or a Settings form, every 30s while idle). Memoizing the
  // element so its reference is stable makes React bail out of re-rendering that subtree on
  // an unrelated RootLayout re-render — real navigation still updates it because
  // `react-router`'s route-match context change propagates to `Outlet`'s consumer regardless
  // of this parent-level bailout (React always walks memoized subtrees to reach context
  // consumers, so this cannot go stale on navigation).
  const outlet = useMemo(() => <Outlet />, []);

  return (
    <div className={styles['shell']}>
      <OfflineBanner />
      <HeaderBar onOpenProfileMenu={() => setProfileMenuOpen(true)} />

      {/* Desktop Left Sidebar (Terminal Minimalist) */}
      <nav className={styles['nav']} aria-label="Primary">
        <div className={styles['brandRow']}>
          <Link to="/" className={styles['brand']}>
            patches
          </Link>
        </div>

        <div className={styles['navGroup']}>
          <NavLink to="/" end className={NAV_LINK_CLASS}>
            <HomeIcon className={styles['navIcon']} />
            <span className={styles['navLabel']}>Home</span>
          </NavLink>

          <NavLink to="/search" className={NAV_LINK_CLASS}>
            <SearchIcon className={styles['navIcon']} />
            <span className={styles['navLabel']}>Search</span>
          </NavLink>

          {session ? (
            <NavLink to="/compose" className={NAV_LINK_CLASS}>
              <ComposeIcon className={styles['navIcon']} />
              <span className={styles['navLabel']}>Compose</span>
            </NavLink>
          ) : null}

          <NavLink
            to="/notifications"
            className={NAV_LINK_CLASS}
            aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
          >
            <div className={styles['iconBadgeWrap']}>
              <BellIcon className={styles['navIcon']} />
              {unreadCount > 0 ? (
                <span className={styles['unreadBadge']} aria-hidden="true">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              ) : null}
            </div>
            <span className={styles['navLabel']}>Notifications</span>
          </NavLink>

          {session ? (
            <>
              <NavLink to="/messages" className={NAV_LINK_CLASS}>
                <MessageIcon className={styles['navIcon']} />
                <span className={styles['navLabel']}>Messages</span>
              </NavLink>

              <NavLink to="/bookmarks" className={NAV_LINK_CLASS}>
                <BookmarkIcon className={styles['navIcon']} />
                <span className={styles['navLabel']}>Bookmarks</span>
              </NavLink>

              <NavLink to="/settings/profile" className={NAV_LINK_CLASS}>
                <SettingsIcon className={styles['navIcon']} />
                <span className={styles['navLabel']}>Settings</span>
              </NavLink>

              <NavLink to="/appeals" className={NAV_LINK_CLASS}>
                <ScaleIcon className={styles['navIcon']} />
                <span className={styles['navLabel']}>Appeals</span>
              </NavLink>
            </>
          ) : null}

          <NavLink to="/moderation/log" className={NAV_LINK_CLASS}>
            <ShieldIcon className={styles['navIcon']} />
            <span className={styles['navLabel']}>Mod log</span>
          </NavLink>
        </div>

        {/* Desktop Primary Compose Button */}
        {session ? (
          <Link to="/compose" className={styles['desktopComposeButton']}>
            <ComposeIcon size={18} />
            <span>New Post</span>
          </Link>
        ) : null}

        {/* Desktop Sidebar Bottom User Card */}
        <div className={styles['bottomNavArea']}>
          {session ? (
            <button
              type="button"
              className={styles['userCardButton']}
              onClick={() => setProfileMenuOpen((open) => !open)}
              aria-label="More"
              aria-expanded={profileMenuOpen}
              aria-controls="profile-dropdown-menu"
            >
              {session.actor.avatar?.url ? (
                <img src={session.actor.avatar.url} alt="" className={styles['sidebarAvatar']} />
              ) : (
                <div className={styles['sidebarAvatarPlaceholder']}>
                  {session.actor.handle.slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className={styles['userCardDetails']}>
                <span className={styles['userCardName']}>
                  {session.actor.displayName || session.actor.handle}
                </span>
                <span className={styles['userCardHandle']}>@{session.actor.handle}</span>
              </div>
            </button>
          ) : (
            <div className={styles['guestSidebarLinks']}>
              <NavLink to="/login" className={NAV_LINK_CLASS}>
                <UserIcon className={styles['navIcon']} />
                <span className={styles['navLabel']}>Sign in</span>
              </NavLink>
              <NavLink to="/register" className={NAV_LINK_CLASS}>
                <span className={styles['navLabel']}>Register</span>
              </NavLink>
              <button
                type="button"
                className={styles['moreHiddenTrigger']}
                onClick={() => setProfileMenuOpen((open) => !open)}
                aria-label="More"
                aria-expanded={profileMenuOpen}
              >
                More
              </button>
            </div>
          )}
        </div>

        {/* Accessible destinations block for screen readers */}
        {profileMenuOpen ? (
          <div
            id="mobile-more-menu"
            className={styles['moreMenuFallback']}
            role="group"
            aria-label="More destinations"
          >
            <NavLink
              to="/moderation/log"
              className={NAV_LINK_CLASS}
              onClick={() => setProfileMenuOpen(false)}
            >
              Mod log
            </NavLink>
            {/* Explicit fallback for shake-to-report (#299): shake can be missed, blocked by
                iOS's own "Undo Typing" prompt, or simply not discovered — this entry works
                regardless of gesture-permission state or platform. */}
            <NavLink
              to="/report"
              className={NAV_LINK_CLASS}
              onClick={() => setProfileMenuOpen(false)}
            >
              Report a problem
            </NavLink>
            {session ? (
              <>
                <NavLink
                  to="/bookmarks"
                  className={NAV_LINK_CLASS}
                  onClick={() => setProfileMenuOpen(false)}
                >
                  Bookmarks
                </NavLink>
                <NavLink
                  to="/messages"
                  className={NAV_LINK_CLASS}
                  onClick={() => setProfileMenuOpen(false)}
                >
                  Messages
                </NavLink>
                <NavLink
                  to={`/@${session.actor.handle}`}
                  className={NAV_LINK_CLASS}
                  onClick={() => setProfileMenuOpen(false)}
                >
                  Profile
                </NavLink>
                <NavLink
                  to="/settings/profile"
                  className={NAV_LINK_CLASS}
                  onClick={() => setProfileMenuOpen(false)}
                >
                  Settings
                </NavLink>
                <NavLink
                  to="/appeals"
                  className={NAV_LINK_CLASS}
                  onClick={() => setProfileMenuOpen(false)}
                >
                  Appeals
                </NavLink>
                <button
                  type="button"
                  className={styles['navLink']}
                  onClick={() => {
                    setProfileMenuOpen(false);
                    void logoutCurrentSession().then(() => navigate('/'));
                  }}
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <NavLink
                  to="/login"
                  className={NAV_LINK_CLASS}
                  onClick={() => setProfileMenuOpen(false)}
                >
                  Sign in
                </NavLink>
                <NavLink
                  to="/register"
                  className={NAV_LINK_CLASS}
                  onClick={() => setProfileMenuOpen(false)}
                >
                  Register
                </NavLink>
              </>
            )}
          </div>
        ) : null}
      </nav>

      {/* Floating Right-Thumb Radial Fan-Out FAB (Mobile) */}
      {session ? <ThumbNavFab unreadCount={unreadCount} /> : null}

      {/* Sleek Terminal-Style Profile Dropdown & Sheet */}
      <ProfileMenu isOpen={profileMenuOpen} onClose={() => setProfileMenuOpen(false)} />

      <main className={styles['main']}>
        <DemoBanner />
        <PrivacyNoticeBanner />
        {outlet}
      </main>

      <footer className={styles['footer']}>
        <small>
          patches web{' '}
          <code title={`built ${__PATCHES_WEB_BUILT_AT__}`}>{__PATCHES_WEB_VERSION__}</code>
          {' · '}
          <a
            href="https://github.com/alliecatowo/patches"
            target="_blank"
            rel="noopener noreferrer"
          >
            source
          </a>
          {' · '}
          <a href="https://patches-site.pages.dev" target="_blank" rel="noopener noreferrer">
            docs
          </a>
        </small>
      </footer>

      <dialog
        ref={helpRef}
        className={styles['helpDialog']}
        aria-labelledby="keyboard-shortcuts-title"
      >
        <h2 id="keyboard-shortcuts-title">Keyboard shortcuts</h2>
        <p>
          <kbd className={styles['helpKbd']}>j</kbd>/<kbd className={styles['helpKbd']}>k</kbd> move
          between posts
        </p>
        <p>
          <kbd className={styles['helpKbd']}>l</kbd> like focused post
        </p>
        <p>
          <kbd className={styles['helpKbd']}>c</kbd> compose new post
        </p>
        <p>
          <kbd className={styles['helpKbd']}>/</kbd> search
        </p>
        <p>
          <kbd className={styles['helpKbd']}>?</kbd> toggle help
        </p>
        <form method="dialog" style={{ marginTop: '1rem' }}>
          <button
            type="submit"
            style={{
              padding: '0.4rem 0.8rem',
              borderRadius: 'var(--radius)',
              background: 'var(--bg-raised)',
              border: '1px solid var(--border)',
            }}
          >
            Close
          </button>
        </form>
      </dialog>
    </div>
  );
}
